import React, { useEffect, useContext, useState, useRef } from 'react';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { EditableFieldsContext } from '../../context';
import { FamilyAddress } from 'common/api/crm/family/address/models/FamilyAddress';
import { LoadingOverlay, openLoader, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { animated, useTransition } from 'react-spring';
import { addAddress } from 'common/api/crm/family/address';
import { Formik, Form } from 'formik';
import { FormikTextField } from 'components/fields/FormikTextField';
import { FormikSelectField } from 'components/fields/FormikSelectField';
import {
    addressLine2Validator,
    addressLine1Validator,
    cityValidator,
    stateValidator,
    zipCodeValidator,
} from 'common/modules/form-validation/validators';
import { stateOptions } from 'common/modules/form-validation/utils/stateOptions';

import { Btn } from 'components/btn/Btn';
import { useAlert } from 'modules/alerts';
import { SERVICE_UNAVAILABLE_MESSAGE } from 'config';
import { UserPlaidWrapper } from 'features/coordinator/enrollment/steps/PlaidButton/PlaidButton';
import { SetUserAchAccountBody, setUserAchAccount, AchAccountBody } from 'common/api/e-comm/ach';
import { PaymentOptionsContext, PaymentOptionsContextProps } from './context';

interface AddressFields {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
}

const NewAddressForm = ({
    setCreateMode,
    createMode,
    refreshAddresses,
    loader,
}: Pick<
    PaymentOptionsContextProps,
    'setCreateMode' | 'createMode' | 'refreshAddresses' | 'loader'
>) => {
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const [errorExists, setErrorExists] = useState(false);

    const transitions = useTransition(createMode, {
        from: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
        enter: { opacity: 1, transform: 'translate2d(0%, 100%)', maxHeight: '1000px' },
        leave: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
    });

    const initialValues = {
        id: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
    };

    const onSubmit = async (values: AddressFields) => {
        const payload = {
            ...values,
            id: initialValues.id || '',
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            phoneNumber: '', // we don't have access of the phoneNumber, where should we get it from?
        };

        try {
            openLoader(loader?.current as LoadingOverlay);
            await addAddress(payload as FamilyAddress);
            refreshAddresses();
            closeLoader(loader?.current as LoadingOverlay);
            setCreateMode(false);
        } catch (e) {
            setErrorExists(true);
        }
    };

    return transitions(
        (styles_, item) =>
            item && (
                <animated.div style={styles_}>
                    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
                        <Form>
                            <div className="flex flex-col mb-5">
                                <div className="mb-5 flex flex-row">
                                    <div className="mr-2 flex-1">
                                        <FormikTextField
                                            name="address1"
                                            label="Address"
                                            validate={addressLine1Validator}
                                        />
                                    </div>
                                    <div className="w-16">
                                        <FormikTextField
                                            name="address2"
                                            label="Ste/Apt/Attn"
                                            validate={addressLine2Validator}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-row mb-5">
                                    <div className="flex-1 mr-2">
                                        <FormikTextField
                                            name="city"
                                            label="City"
                                            validate={cityValidator}
                                        />
                                    </div>
                                    <div className="w-16">
                                        <FormikSelectField
                                            label="State"
                                            name="state"
                                            validate={stateValidator}
                                        >
                                            <option value="" />
                                            {stateOptions.map((o) => (
                                                <option key={o.value}>{o.value}</option>
                                            ))}
                                        </FormikSelectField>
                                    </div>
                                </div>
                                <div>
                                    <FormikTextField
                                        label="Zip"
                                        name="zipCode"
                                        validate={zipCodeValidator}
                                    />
                                </div>
                            </div>
                            {errorExists && (
                                <p className="text-sm mb-3 text-error">
                                    Unable to update your information at this time
                                </p>
                            )}
                            <Btn type="submit">Save New Address</Btn>
                        </Form>
                    </Formik>
                </animated.div>
            )
    );
};

/*
 * Renders the Address form when `createMode` is true.
 * It renders a border-top with a `close` button given that user has _addresses_.
 * However, this is not the case when the user _does not have_ addresses.
 */
export const BankEditFieldComponent = () => {
    const { setFieldToEdit } = useContext(EditableFieldsContext);
    const {
        selectedAddress,
        createMode,
        setCreateMode,
        refreshAddresses,
        addresses,
        loader,
        reloadAchInfo,
    } = useContext(PaymentOptionsContext);
    const hasNoAddressSelected = !selectedAddress;
    const hasNoAddresses = addresses.length === 0;
    const selectedAddressRef = useRef<string>('');
    const alert = useAlert();

    useEffect(() => {
        selectedAddressRef.current = selectedAddress;
    }, [selectedAddress]);

    const close = () => {
        setCreateMode(false);
    };

    const onPlaidLinkSuccess = async <T,>(payload: T) => {
        const userAchPayload: SetUserAchAccountBody = {
            ...(payload as unknown as AchAccountBody),
            addressId: selectedAddressRef.current,
        };

        openLoader(loader?.current as LoadingOverlay);
        const response = await setUserAchAccount(userAchPayload);
        await reloadAchInfo();
        closeLoader(loader?.current as LoadingOverlay);

        // NOTE: Remove addr from local-storage in case we have one stored
        // not needed for this step.
        localStorage.removeItem('addr');
        setFieldToEdit(''); // no longer show edit-mode

        if (response.error) {
            alert({ message: SERVICE_UNAVAILABLE_MESSAGE });
        }
    };

    return (
        <div className={!hasNoAddresses && createMode ? 'border-t mt-5 max-w-lg' : 'max-w-lg'}>
            {!hasNoAddresses && createMode && (
                <div className="flex justify-end py-5">
                    <p
                        onClick={close}
                        className="font-semibold underline text-brand text-xs cursor-pointer"
                    >
                        Close
                    </p>
                </div>
            )}
            <NewAddressForm
                setCreateMode={setCreateMode}
                createMode={createMode}
                refreshAddresses={refreshAddresses}
                loader={loader}
            />
            {!createMode && (
                <div className="mt-5">
                    <UserPlaidWrapper
                        addressId={selectedAddress}
                        onSuccess={onPlaidLinkSuccess}
                        isOAuth={false}
                        onExit={() => null}
                    >
                        {(openPlaid, isPlaidReady) => (
                            <Btn
                                onClick={openPlaid}
                                disabled={!isPlaidReady || hasNoAddressSelected}
                            >
                                Continue With Plaid
                            </Btn>
                        )}
                    </UserPlaidWrapper>
                </div>
            )}
        </div>
    );
};
